import React, { useState, useRef, useEffect } from 'react';

const panelStyle = {
  width: 280,
  background: '#1a1a2e',
  borderLeft: '1px solid rgba(102,126,234,0.15)',
  padding: 16,
  overflowY: 'auto',
  color: '#eee',
  fontFamily: 'sans-serif',
  borderRadius: '8px 0 0 8px',
};

const btnStyle = {
  padding: '10px 16px',
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  cursor: 'pointer',
  fontSize: 13,
  fontWeight: 600,
  width: '100%',
  marginTop: 10,
  transition: 'opacity 0.2s',
};

const presetBtnStyle = (active) => ({
  padding: '8px 14px',
  background: active ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#0f0f1a',
  color: active ? '#fff' : '#999',
  border: active ? 'none' : '1px solid #333',
  borderRadius: 6,
  cursor: 'pointer',
  fontSize: 12,
  fontWeight: active ? 600 : 400,
  flex: 1,
  transition: 'all 0.2s',
});

const PRESETS = [
  { label: 'Light', value: 0.25 },
  { label: 'Medium', value: 0.5 },
  { label: 'Strong', value: 0.85 },
];

export default function SkinEnhancePanel({ editor }) {
  const [smoothing, setSmoothing] = useState(0.5);
  const [useGfpgan, setUseGfpgan] = useState(false);
  const [activePreset, setActivePreset] = useState('Medium');
  const mountedRef = useRef(false);
  const timerRef = useRef(null);

  // Auto-apply on smoothing slider change (debounced 500ms), skip initial mount
  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      editor.skinEnhance({ smoothing, use_gfpgan: useGfpgan });
    }, 500);
    return () => clearTimeout(timerRef.current);
  }, [smoothing]);

  const handlePreset = (preset) => {
    setActivePreset(preset.label);
    setSmoothing(preset.value);
    // Direct apply for preset clicks
    editor.skinEnhance({ smoothing: preset.value, use_gfpgan: useGfpgan });
  };

  const handleQuickEnhance = () => {
    editor.skinEnhance({ smoothing, use_gfpgan: useGfpgan });
  };

  const handleVariants = () => {
    editor.skinEnhanceVariants();
    editor.setActiveTool('skinEnhanceVariants');
  };

  return (
    <div style={panelStyle}>
      <h3 style={{ margin: '0 0 14px', fontSize: 14, color: '#ddd' }}>✨ Skin Enhancement</h3>

      {/* Note */}
      <div style={{
        padding: '8px 10px', background: '#0f0f1a', borderRadius: 6,
        marginBottom: 14, border: '1px solid #333', fontSize: 11, color: '#888',
      }}>
        💡 Works best with face photos
      </div>

      {/* Presets */}
      <div style={{ fontSize: 11, color: '#666', marginBottom: 8 }}>Quick Presets</div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
        {PRESETS.map((p) => (
          <button
            key={p.label}
            style={presetBtnStyle(activePreset === p.label)}
            onClick={() => handlePreset(p)}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div style={{ height: 1, background: '#333', margin: '12px 0' }} />

      {/* Smoothing slider */}
      <div style={{ marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <span style={{ fontSize: 11, color: '#888', width: 70 }}>Smoothing</span>
          <input
            type="range" min={0} max={1} step={0.05} value={smoothing}
            onChange={(e) => {
              const val = Number(e.target.value);
              setSmoothing(val);
              // Update active preset label
              const match = PRESETS.find((p) => Math.abs(p.value - val) < 0.01);
              setActivePreset(match ? match.label : '');
            }}
            style={{ flex: 1, accentColor: '#667eea' }}
          />
          <span style={{ fontSize: 11, color: '#ccc', width: 30, textAlign: 'right' }}>
            {smoothing.toFixed(2)}
          </span>
        </div>
      </div>

      <label style={{
        display: 'flex', alignItems: 'center', gap: 8,
        fontSize: 12, color: '#ccc', marginBottom: 12,
      }}>
        <input type="checkbox" checked={useGfpgan} onChange={(e) => setUseGfpgan(e.target.checked)} />
        Use AI Face Restoration (GFPGAN)
      </label>

      <button style={btnStyle} onClick={handleQuickEnhance}>
        ⚡ Quick Enhance
      </button>
      <button
        style={{ ...btnStyle, background: '#0f0f1a', border: '1px solid #333', color: '#ccc', fontWeight: 400 }}
        onClick={handleVariants}
      >
        Generate Variants
      </button>
    </div>
  );
}
